import type { InlineChatChartSpec } from '../components/chart/InlineChatLineChart';

const FENCE_BLOCK_RE = /```([A-Za-z0-9_+-]*)[ \t]*\r?\n([\s\S]*?)```/g;

function hasSeriesKeys(spec: InlineChatChartSpec): boolean {
  if (Array.isArray(spec.y_keys) && spec.y_keys.length > 0) return true;
  return typeof spec.y_key === 'string' && spec.y_key.length > 0;
}

export function isInlineChatChartSpec(obj: unknown): obj is InlineChatChartSpec {
  if (typeof obj !== 'object' || obj === null) return false;
  const spec = obj as InlineChatChartSpec;
  return (
    spec.type === 'line' &&
    typeof spec.x_key === 'string' &&
    hasSeriesKeys(spec) &&
    Array.isArray(spec.data) &&
    spec.data.length > 0
  );
}

/** Relabel fenced blocks whose body is a chart spec JSON to ```chart. */
export function normalizeChartFences(text: string): string {
  if (!text) return '';

  return text.replace(FENCE_BLOCK_RE, (match, lang: string, body: string) => {
    if (lang.toLowerCase() === 'chart') return match;
    const stripped = body.trim();
    if (!stripped.startsWith('{')) return match;
    try {
      const spec = JSON.parse(stripped) as unknown;
      if (isInlineChatChartSpec(spec)) {
        return `\`\`\`chart\n${stripped}\n\`\`\``;
      }
    } catch {
      // Keep the original fence when JSON is invalid.
    }
    return match;
  });
}

function findJsonObjectEnd(text: string, start: number): number {
  if (text[start] !== '{') return -1;

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth === 0) return i + 1;
    }
  }

  return -1;
}

function isInsideFencedBlock(text: string, index: number): boolean {
  const before = text.slice(0, index);
  return (before.match(/```/g) ?? []).length % 2 === 1;
}

const INLINE_CHART_JSON_START_RE = /\{\s*"type"\s*:\s*"line"/g;

export function findInlineChartJsonStart(text: string, fromIndex = 0): number {
  INLINE_CHART_JSON_START_RE.lastIndex = fromIndex;
  const match = INLINE_CHART_JSON_START_RE.exec(text);
  return match?.index ?? -1;
}

function tryParseInlineChartSpec(
  text: string,
  start: number,
): { spec: InlineChatChartSpec; end: number } | null {
  const end = findJsonObjectEnd(text, start);
  if (end === -1) return null;

  try {
    const spec = JSON.parse(text.slice(start, end)) as unknown;
    if (!isInlineChatChartSpec(spec)) return null;
    return { spec, end };
  } catch {
    return null;
  }
}

/** Wrap bare inline chart JSON (no fence) so the chat markdown parser can render it. */
export function wrapBareInlineChartJson(text: string): string {
  let result = text;
  let searchFrom = 0;

  while (searchFrom < result.length) {
    const idx = findInlineChartJsonStart(result, searchFrom);
    if (idx === -1) break;
    if (isInsideFencedBlock(result, idx)) {
      searchFrom = idx + 1;
      continue;
    }

    const parsed = tryParseInlineChartSpec(result, idx);
    if (!parsed) {
      searchFrom = idx + 1;
      continue;
    }

    const { spec, end } = parsed;
    const candidate = JSON.stringify(spec);
    const prefix = result.slice(0, idx).trimEnd();
    const suffix = result.slice(end).trimStart();
    const wrapped = `\`\`\`chart\n${candidate}\n\`\`\``;
    result = [prefix, wrapped, suffix].filter((part) => part.length > 0).join('\n\n');
    searchFrom = prefix.length + wrapped.length + 2;
  }

  return result;
}

export function prepareChatMarkdownForCharts(content: string): string {
  return wrapBareInlineChartJson(normalizeChartFences(content));
}
